/**
 * The main cleaning pass: messy HTML in, structural HTML out.
 *
 * Everything happens on a detached DOM built by `parseToBody`, so the passes
 * below can be plain DOM code instead of regex-on-a-string guesswork.
 */

import type { CleanOptions } from './types';
import { parseToBody } from './sanitize';

/**
 * Attributes worth keeping, per tag. Anything not listed here is dropped when
 * "strip other attributes" is on. Presentational leftovers (`dir`, `align`,
 * `bgcolor`, `width` on a `<td>`, …) are intentionally absent.
 */
const KEEP_ATTRS: Record<string, string[]> = {
  a: ['href', 'title', 'target', 'rel'],
  img: ['src', 'alt', 'title', 'width', 'height', 'srcset', 'loading'],
  source: ['src', 'srcset', 'type', 'media'],
  ol: ['start', 'reversed', 'type'],
  li: ['value'],
  td: ['colspan', 'rowspan'],
  th: ['colspan', 'rowspan', 'scope'],
  colgroup: ['span'],
  col: ['span'],
  blockquote: ['cite'],
  q: ['cite'],
  time: ['datetime'],
  input: ['type', 'checked', 'disabled'],
  abbr: ['title'],
  code: ['class'], // language-xxx is genuinely useful; filtered further below
  pre: [],
};

/** Elements that hold text on the same line as their neighbours. */
export const INLINE_TAGS = new Set([
  'a', 'abbr', 'b', 'bdi', 'bdo', 'br', 'cite', 'code', 'data', 'dfn', 'em',
  'i', 'kbd', 'mark', 'q', 'rp', 'rt', 'ruby', 's', 'samp', 'small', 'span',
  'strong', 'sub', 'sup', 'time', 'u', 'var', 'wbr', 'img',
]);

/** Elements that are meaningful even with no text inside. */
const VOID_OR_STANDALONE = new Set([
  'br', 'hr', 'img', 'input', 'source', 'track', 'wbr', 'col', 'area', 'embed', 'iframe', 'video', 'audio',
]);

/** Inline tags that are safe to merge when directly nested in themselves. */
const MERGEABLE = new Set(['strong', 'em', 'b', 'i', 'u', 's', 'span', 'small', 'mark']);

/** Zero-width space/non-joiner/joiner, BOM, word-joiner, soft hyphen. */
const ZERO_WIDTH = /[\u200b-\u200d\ufeff\u2060\u00ad]/g;

/** Non-breaking space, plus the narrow/thin variants Word likes to emit. */
const NBSP = /[\u00a0\u202f\u2007]/g;

/** Clean a raw HTML string according to `opts`. Returns HTML (not tidied). */
export function cleanHtml(html: string, opts: CleanOptions): string {
  const body = parseToBody(html);

  if (opts.stripComments) removeComments(body);
  removeContent(body, opts);
  filterAttributes(body, opts);
  if (opts.semanticTags) applySemanticTags(body);
  if (opts.unwrapSpans) unwrapTags(body, ['span', 'font']);
  if (opts.unwrapDivs) convertDivs(body);
  applyTagAllowlist(body, opts.allowedTags);
  normalizeText(body, opts);
  if (opts.semanticTags) mergeNestedInline(body);
  if (opts.removeEmpty) removeEmptyElements(body);

  return body.innerHTML;
}

// ── Passes ───────────────────────────────────────────────────────────────────

function removeComments(root: ParentNode): void {
  const walker = (root.ownerDocument ?? document).createTreeWalker(
    root as Node,
    NodeFilter.SHOW_COMMENT,
  );
  const doomed: Node[] = [];
  while (walker.nextNode()) doomed.push(walker.currentNode);
  for (const node of doomed) node.parentNode?.removeChild(node);
}

function removeAll(root: ParentNode, selector: string): void {
  for (const el of Array.from(root.querySelectorAll(selector))) el.remove();
}

function removeContent(body: HTMLElement, opts: CleanOptions): void {
  if (opts.stripImages) removeAll(body, 'img, picture, figure, svg');
  if (opts.stripMedia) removeAll(body, 'iframe, video, audio, embed, object, canvas, svg, map, track');
  if (opts.stripFormElements) removeAll(body, 'form, input, button, select, textarea, option, optgroup, fieldset, legend, label, datalist, output, progress, meter');
  if (opts.stripTables) removeAll(body, 'table');
  if (opts.stripLinks) unwrapTags(body, ['a']);
}

function filterAttributes(body: HTMLElement, opts: CleanOptions): void {
  for (const el of Array.from(body.querySelectorAll('*'))) {
    const tag = el.tagName.toLowerCase();
    const keep = KEEP_ATTRS[tag] ?? [];

    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();

      if (name === 'class' || name === 'id') {
        // A `language-…` class on <code> carries real meaning — keep just that.
        if (opts.stripClassesIds) {
          if (tag === 'code' && name === 'class') {
            const lang = attr.value.split(/\s+/).find(c => /^(language|lang)-[\w+#-]+$/i.test(c));
            if (lang) { el.setAttribute('class', lang); continue; }
          }
          el.removeAttribute(attr.name);
        }
        continue;
      }

      if (name === 'style') {
        if (opts.stripInlineStyles) el.removeAttribute(attr.name);
        continue;
      }

      if (name.startsWith('data-')) {
        if (opts.stripDataAttrs) el.removeAttribute(attr.name);
        continue;
      }

      // `start="1"` on an <ol> is what the list already does — pure noise.
      if (opts.stripOtherAttrs && tag === 'ol' && name === 'start' && attr.value.trim() === '1') {
        el.removeAttribute(attr.name);
        continue;
      }

      if (opts.stripOtherAttrs && !keep.includes(name)) {
        el.removeAttribute(attr.name);
      }
    }
  }
}

/** `<b>`→`<strong>`, `<i>`→`<em>`, and drop purely visual wrappers. */
function applySemanticTags(body: HTMLElement): void {
  const swaps: Record<string, string> = { b: 'strong', i: 'em', strike: 's', big: 'span', tt: 'code' };
  for (const [from, to] of Object.entries(swaps)) {
    for (const el of Array.from(body.querySelectorAll(from))) {
      renameElement(el, to);
    }
  }
  unwrapTags(body, ['font', 'center', 'marquee', 'blink', 'nobr']);
}

/** Replace an element with the same-content element under a new tag name. */
function renameElement(el: Element, tag: string): Element {
  const doc = el.ownerDocument;
  const replacement = doc.createElement(tag);
  for (const attr of Array.from(el.attributes)) replacement.setAttribute(attr.name, attr.value);
  while (el.firstChild) replacement.appendChild(el.firstChild);
  el.replaceWith(replacement);
  return replacement;
}

/** Remove the given tags but keep their children in place. */
function unwrapTags(body: HTMLElement, tags: string[]): void {
  for (const el of Array.from(body.querySelectorAll(tags.join(',')))) {
    unwrap(el);
  }
}

function unwrap(el: Element): void {
  const parent = el.parentNode;
  if (!parent) return;
  while (el.firstChild) parent.insertBefore(el.firstChild, el);
  parent.removeChild(el);
}

/**
 * `<div>` is a layout box, not structure. If it holds only inline content it
 * becomes a `<p>`; if it holds other blocks it just disappears and its children
 * move up a level. Works innermost-first so nested div soup fully unwinds.
 */
function convertDivs(body: HTMLElement): void {
  const divs = Array.from(body.querySelectorAll('div, section, article, main, aside, header, footer, nav'));
  for (let i = divs.length - 1; i >= 0; i--) {
    const div = divs[i];
    if (!div.isConnected) continue;
    if (holdsOnlyInline(div) && div.textContent?.trim()) {
      renameElement(div, 'p');
    } else {
      unwrap(div);
    }
  }
}

function holdsOnlyInline(el: Element): boolean {
  for (const child of Array.from(el.children)) {
    if (!INLINE_TAGS.has(child.tagName.toLowerCase())) return false;
  }
  return true;
}

/** Optional "only keep these tags" list — anything else is unwrapped. */
function applyTagAllowlist(body: HTMLElement, list: string): void {
  const allowed = new Set(
    list.split(/[\s,]+/).map(t => t.trim().toLowerCase().replace(/^<|>$/g, '')).filter(Boolean),
  );
  if (allowed.size === 0) return;

  for (const el of Array.from(body.querySelectorAll('*'))) {
    if (!el.isConnected) continue;
    const tag = el.tagName.toLowerCase();
    if (allowed.has(tag)) continue;
    // A void element has no children to keep, so unwrapping would just leave a
    // hole — delete it instead.
    if (VOID_OR_STANDALONE.has(tag)) el.remove();
    else unwrap(el);
  }
}

/** Text-node cleanup: nbsp, invisible characters, smart punctuation, spacing. */
function normalizeText(body: HTMLElement, opts: CleanOptions): void {
  const walker = (body.ownerDocument ?? document).createTreeWalker(body, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  while (walker.nextNode()) nodes.push(walker.currentNode as Text);

  for (const node of nodes) {
    // Never reformat the inside of a <pre> — the whitespace is the content.
    if (node.parentElement?.closest('pre')) continue;

    let text = node.data;
    if (opts.convertNbsp) text = text.replace(NBSP, ' ');
    if (opts.stripZeroWidth) text = text.replace(ZERO_WIDTH, '');
    if (opts.normalizeQuotes) text = normalizePunctuation(text);
    if (opts.collapseWhitespace) text = text.replace(/[ \t\r\n\f\v]+/g, ' ');
    node.data = text;
  }

  if (opts.collapseWhitespace) trimBlockEdges(body);
}

export function normalizePunctuation(text: string): string {
  return text
    .replace(/[\u2018\u2019\u201a\u201b\u2032]/g, "'")
    .replace(/[\u201c\u201d\u201e\u201f\u2033]/g, '"')
    .replace(/\u2014/g, '--')
    .replace(/[\u2013\u2012]/g, '-')
    .replace(/\u2026/g, '...')
    .replace(NBSP, ' ');
}

/**
 * After collapsing runs of whitespace every block still tends to start or end
 * with a stray space (`<p> Hello </p>`). Trim just those edges — spaces *between*
 * inline siblings have to stay or words run together.
 */
function trimBlockEdges(body: HTMLElement): void {
  const walk = (el: Element): void => {
    if (el.tagName.toLowerCase() === 'pre') return;
    const isBlock = !INLINE_TAGS.has(el.tagName.toLowerCase());
    if (isBlock) {
      const first = firstTextIn(el);
      if (first) first.data = first.data.replace(/^[ \t]+/, '');
      const last = lastTextIn(el);
      if (last) last.data = last.data.replace(/[ \t]+$/, '');
    }
    for (const child of Array.from(el.children)) walk(child);
  };
  walk(body);
}

/** Both helpers skip `<pre>` — its leading/trailing spaces are content. */
function firstTextIn(el: Element): Text | null {
  for (const node of Array.from(el.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) return node as Text;
    if (node.nodeType === Node.ELEMENT_NODE) {
      if ((node as Element).tagName.toLowerCase() === 'pre') return null;
      const inner = firstTextIn(node as Element);
      if (inner) return inner;
    }
  }
  return null;
}

function lastTextIn(el: Element): Text | null {
  const kids = Array.from(el.childNodes);
  for (let i = kids.length - 1; i >= 0; i--) {
    const node = kids[i];
    if (node.nodeType === Node.TEXT_NODE) return node as Text;
    if (node.nodeType === Node.ELEMENT_NODE) {
      if ((node as Element).tagName.toLowerCase() === 'pre') return null;
      const inner = lastTextIn(node as Element);
      if (inner) return inner;
    }
  }
  return null;
}

/**
 * Pasted content is full of `<strong><strong>x</strong></strong>` and adjacent
 * `<strong>a</strong><strong>b</strong>` pairs. Flatten and join them.
 */
function mergeNestedInline(body: HTMLElement): void {
  // Same tag directly inside itself → drop the inner one.
  for (const tag of MERGEABLE) {
    for (const el of Array.from(body.querySelectorAll(`${tag} ${tag}`))) {
      if (el.parentElement?.tagName.toLowerCase() === tag) unwrap(el);
    }
  }

  // Adjacent identical siblings → merge into one.
  for (const el of Array.from(body.querySelectorAll('*'))) {
    if (!el.isConnected) continue;
    const tag = el.tagName.toLowerCase();
    if (!MERGEABLE.has(tag)) continue;
    let next = el.nextSibling;
    while (next && next.nodeType === Node.ELEMENT_NODE
      && (next as Element).tagName.toLowerCase() === tag
      && sameAttributes(el, next as Element)) {
      const sibling = next as Element;
      next = sibling.nextSibling;
      while (sibling.firstChild) el.appendChild(sibling.firstChild);
      sibling.remove();
    }
  }
}

function sameAttributes(a: Element, b: Element): boolean {
  if (a.attributes.length !== b.attributes.length) return false;
  for (const attr of Array.from(a.attributes)) {
    if (b.getAttribute(attr.name) !== attr.value) return false;
  }
  return true;
}

/**
 * Drop elements with nothing in them. Runs innermost-first and repeats, so an
 * empty `<span>` inside an otherwise-empty `<p>` takes the `<p>` with it.
 */
function removeEmptyElements(body: HTMLElement): void {
  let changed = true;
  let guard = 0;
  while (changed && guard++ < 20) {
    changed = false;
    const all = Array.from(body.querySelectorAll('*'));
    for (let i = all.length - 1; i >= 0; i--) {
      const el = all[i];
      if (!el.isConnected) continue;
      const tag = el.tagName.toLowerCase();
      if (VOID_OR_STANDALONE.has(tag)) continue;
      if (tag === 'td' || tag === 'th') continue; // an empty cell holds the grid together
      if (el.children.length === 0 && !el.textContent?.trim()) {
        el.remove();
        changed = true;
      }
    }
  }
}
